import type { ImgHTMLAttributes } from 'react';

import { cn } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BankSlug =
  | 'azteca'
  | 'bajio'
  | 'banamex'
  | 'bancoppel'
  | 'banorte'
  | 'banregio'
  | 'bbva'
  | 'hsbc'
  | 'inbursa'
  | 'mercadopago'
  | 'nu'
  | 'santander'
  | 'scotiabank';

// ─── Asset paths ──────────────────────────────────────────────────────────────

export const BANK_LOGO_PATHS: Record<BankSlug, string> = {
  azteca: '/assets/images/banks/banco-azteca.svg',
  bajio: '/assets/images/banks/banco-del-bajio.svg',
  banamex: '/assets/images/banks/banamex.svg',
  bancoppel: '/assets/images/banks/bancoppel.svg',
  banorte: '/assets/images/banks/banorte.svg',
  banregio: '/assets/images/banks/banregio.svg',
  bbva: '/assets/images/banks/bbva.svg',
  hsbc: '/assets/images/banks/hsbc.svg',
  inbursa: '/assets/images/banks/inbursa.svg',
  mercadopago: '/assets/images/banks/mercadopago.svg',
  nu: '/assets/images/banks/nu.svg',
  santander: '/assets/images/banks/santander.svg',
  scotiabank: '/assets/images/banks/scotia.svg',
};

/**
 * Versión NEGATIVA del logo (monocroma/blanca) para usar sobre fondos de color
 * — la usa `BankCircle` por defecto. `inbursa` no tiene versión negativa, así
 * que cae al logo normal.
 */
export const BANK_LOGO_NEGATIVE_PATHS: Record<BankSlug, string> = {
  azteca: '/assets/images/banks/banco-azteca-negative.svg',
  bajio: '/assets/images/banks/banco-del-bajio-negative.svg',
  banamex: '/assets/images/banks/banamex-negative.svg',
  bancoppel: '/assets/images/banks/bancoppel-negative.svg',
  banorte: '/assets/images/banks/banorte-negative.svg',
  banregio: '/assets/images/banks/banregio-negative.svg',
  bbva: '/assets/images/banks/bbva-negative.svg',
  hsbc: '/assets/images/banks/hsbc-negative.svg',
  inbursa: '/assets/images/banks/inbursa.svg', // sin negativa → fallback normal
  mercadopago: '/assets/images/banks/mercadopago-negative.svg',
  nu: '/assets/images/banks/nu-negative.svg',
  santander: '/assets/images/banks/santander-negative.svg',
  scotiabank: '/assets/images/banks/scotia-negative.svg',
};

// ─── Size variants ────────────────────────────────────────────────────────────

const SIZE_CLASSES = {
  xs: 'h-4 w-auto',
  sm: 'h-6 w-auto',
  md: 'h-8 w-auto',
  lg: 'h-10 w-auto',
  xl: 'h-12 w-auto',
} as const;

type BankLogoSize = keyof typeof SIZE_CLASSES;

// ─── Main component ───────────────────────────────────────────────────────────

interface BankLogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  bank: BankSlug;
  size?: BankLogoSize;
}

export function BankLogo({ bank, size = 'md', className, ...props }: BankLogoProps) {
  const src = BANK_LOGO_PATHS[bank];
  return (
    <img
      src={src}
      alt={bank}
      className={cn(SIZE_CLASSES[size], 'object-contain', className)}
      {...props}
    />
  );
}

// ─── Individual bank variables ────────────────────────────────────────────────

type BankImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'>;

function makeLogo(slug: BankSlug) {
  function BankLogoVariant({ className, ...props }: BankImgProps) {
    return (
      <img
        src={BANK_LOGO_PATHS[slug]}
        alt={slug}
        className={cn('h-8 w-auto object-contain', className)}
        {...props}
      />
    );
  }
  BankLogoVariant.displayName = `${slug.charAt(0).toUpperCase()}${slug.slice(1)}Logo`;
  return BankLogoVariant;
}

export const AztecaLogo = makeLogo('azteca');
export const BajioLogo = makeLogo('bajio');
export const BanamexLogo = makeLogo('banamex');
export const BancoppelLogo = makeLogo('bancoppel');
export const BanorteLogo = makeLogo('banorte');
export const BanregioLogo = makeLogo('banregio');
export const BbvaLogo = makeLogo('bbva');
export const HsbcLogo = makeLogo('hsbc');
export const InbursaLogo = makeLogo('inbursa');
export const MercadoPagoLogo = makeLogo('mercadopago');
export const NuLogo = makeLogo('nu');
export const SantanderLogo = makeLogo('santander');
export const ScotiabankLogo = makeLogo('scotiabank');
